import * as React from "react";
import styled, { css, useTheme } from "styled-components";

import { Heading } from "@ui/Atoms/Headings";
import { Paragraph } from "@ui/Atoms/Paragraph";
import { TestWrapper } from "@ui/Atoms/TestWrapper";

import { Markdown } from "@containers/Markdown";

type BaseProps = {
  children?: React.ReactNode;
  id?: string;
  className?: string;
  testId?: string;
};

type DefaultProps = {
  variant?: "v1" | "v3";
  title?: string | React.ReactElement;
  subtitle?: string;
  withSmallTitle?: boolean;
  isThin?: boolean;
  isTransparent?: boolean;
  noMarginsMobile?: boolean;
  textAlignLg?: string;
};

type NewProps = {
  variant: "v2";
  title: string;
  subtitle: string;
  icon?: React.ReactElement;
  label?: React.ReactElement;
};

type Props = BaseProps & (DefaultProps | NewProps);

const OptionPanel = (props: Props): React.ReactElement => {
  const theme = useTheme();

  if (props.variant === "v2") {
    return (
      <TestWrapper testId={props.testId}>
        <OptionPanelSection {...props} $variant="v2">
          <HeadingWrapper>
            <Title>
              {props.icon && <IconWrapper>{props.icon}</IconWrapper>}
              <StyledHeading hasSubtitle={false} noMarginBotton>
                <Markdown>{props.title}</Markdown>
              </StyledHeading>
              {props.label}
            </Title>
            <Paragraph type="body2" weight="normal" noMarginBottom>
              {props.subtitle}
            </Paragraph>
          </HeadingWrapper>
          {props.children}
        </OptionPanelSection>
      </TestWrapper>
    );
  }

  const { title, ...otherProps } = props as BaseProps & DefaultProps;

  return (
    <TestWrapper testId={props.testId}>
      <OptionPanelSection
        {...otherProps}
        $variant={props.variant === "v3" ? "v3" : "v1"}
      >
        {title && (
          <StyledHeading
            withSmallTitle={otherProps.withSmallTitle}
            hasSubtitle={!!otherProps.subtitle}
            textAlignLg={otherProps.textAlignLg}
          >
            {typeof title === "string" ? <Markdown>{title}</Markdown> : title}
          </StyledHeading>
        )}
        {otherProps.subtitle && (
          <Paragraph
            type="body2"
            color={theme.palette.grey600}
            textAlignLg={otherProps.textAlignLg}
            noMarginBottom
          >
            {otherProps.subtitle}
          </Paragraph>
        )}
        {props.children}
      </OptionPanelSection>
    </TestWrapper>
  );
};

const OptionPanelSection = styled.div<{
  isThin?: boolean;
  isTransparent?: boolean;
  noMarginsMobile?: boolean;
  $variant: "v1" | "v2" | "v3";
}>`
  margin: ${props => (props.isThin ? "0" : props.theme.margins.base)} 0;

  padding: ${props =>
    props.isThin
      ? `${props.theme.margins.base} ${props.theme.margins.base_x2}`
      : `${props.theme.margins.base_x4} ${props.theme.margins.base_x3}`};

  border-radius: ${props => props.theme.buttons.borderRadius};

  ${props =>
    props.noMarginsMobile &&
    css`
      margin-left: -${props.theme.margins.base_x2};
      margin-right: -${props.theme.margins.base_x2};
    `}

  ${props =>
    !props.isTransparent &&
    `
    background: ${props.theme.palette.white};
  `}

  ${props => props.theme.breakpoints.tablet} {
    padding: ${props =>
      props.isThin
        ? `${props.theme.margins.base} ${props.theme.margins.base_x2}`
        : props.theme.margins.base_x5};
  }

  ${props => props.theme.breakpoints.desktop} {
    margin: ${props => props.theme.margins.base} 0;
  }

  ${props =>
    props.$variant === "v2" &&
    css`
      margin-top: 0;
      margin-bottom: 0;
      display: flex;
      gap: ${({ theme }) => theme.margins.base};
    `}

  ${props =>
    props.$variant === "v3" &&
    css`
      margin-top: ${props => props.theme.spacing.spacing_24};
      margin-bottom: ${props => props.theme.spacing.spacing_24};
      border: 1px solid ${props => props.theme.palette.grey800};
      border-radius: 12px;

      ${props => props.theme.breakpoints.desktop} {
        margin-top: ${props => props.theme.spacing.spacing_24};
        margin-bottom: ${props => props.theme.spacing.spacing_24};
      }
    `}
`;

const IconWrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.grey20};
  border-radius: ${({ theme }) => theme.border.radius.half};
  padding: ${({ theme }) => theme.margins.base};
`;

const StyledHeading = styled(Heading).attrs({ type: "h6" })<{
  withSmallTitle?: boolean;
  hasSubtitle?: boolean;
}>`
  margin-bottom: ${props =>
    props.noMarginBotton
      ? 0
      : props.hasSubtitle
      ? props.theme.margins.base
      : props.theme.margins.base_x4};

  ${props => props.theme.breakpoints.desktop} {
    font-size: ${props => props.withSmallTitle && props.theme.fonts.sizes.body};
  }
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.base};
`;

const Title = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.margins.base};
  width: 100%;
  font-size: ${({ theme }) => theme.fonts.sizes.h6};
  font-weight: ${({ theme }) => theme.fonts.weights.semibold};
  line-height: ${({ theme }) => theme.fonts.lineHeights.h6};
  align-items: center;
`;

export { OptionPanel };
